import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
} from '@mui/material';
import { Add, Delete, Edit, WarningAmberOutlined, CheckCircleOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  SubscriptionPlanDto,
  useAdminPlansQuery,
  useCreateAdminPlanMutation,
  useUpdateAdminPlanMutation,
  useDeleteAdminPlanMutation,
} from '../../API/Subscriptions';
import { useModalDisclosure } from '@hooks/useDisclosure';
import { failed, success, warning } from '@components/Common/Toastify';
import StripeCatalogField, { StripeLinkCell } from './StripeCatalogField';
import PriceField from './PriceField';

/**
 * Currencies the server will accept. A dropdown rather than free text because
 * the currency is baked into an immutable Stripe price — a typo cannot be
 * edited away, only superseded by a whole new price.
 *
 * Must stay in step with SUBSCRIPTION_ALLOWED_CURRENCIES on the server; the
 * server rejects anything outside its own list regardless of what is shown here.
 */
const CURRENCIES = ['AUD', 'USD', 'NZD', 'GBP', 'EUR', 'CAD', 'SGD'];

const BILLING_INTERVALS = [
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
  { label: 'Year', value: 'year' },
];

const getPlanDays = (interval?: string, count: number = 1): number => {
  const c = Math.max(1, Number(count) || 1);
  switch (String(interval || 'month').toLowerCase()) {
    case 'day':
      return c * 1;
    case 'week':
      return c * 7;
    case 'month':
      return c * 30;
    case 'year':
      return c * 365;
    default:
      return c * 30;
  }
};

/**
 * Saving a plan now also creates or updates its Stripe product and price, and
 * some of what Stripe does is consequential but not an error — repricing
 * grandfathers existing subscribers, for one. The server returns those as
 * `stripeWarnings`; swallowing them would hide the most surprising part of the
 * operation behind a bare "Saved".
 */
const showStripeWarnings = (res: any) => {
  const warnings: string[] = res?.stripeWarnings || [];
  warnings.forEach((text) => warning(text));
};


const emptyForm: Partial<SubscriptionPlanDto> = {
  code: '',
  name: '',
  priceMonthly: 0,
  currency: 'USD',
  billingInterval: 'month',
  billingIntervalCount: 1,
  maxAgents: null,
  maxMinutesPerMonth: null,
  maxSmsPerMonth: null,
  trialDurationDays: null,
  stripePriceId: null,
  isActive: true,
  maxSmsNumber: null,
};

const Plans = () => {
  const { data, isLoading, error, refetch } = useAdminPlansQuery({
    config: { keepPreviousData: true },
  });

  const plans: SubscriptionPlanDto[] = useMemo(() => data?.data || [], [data?.data]);

  const createModal = useModalDisclosure();
  const editModal = useModalDisclosure();
  const toggleConfirmModal = useModalDisclosure();

  const [form, setForm] = useState<Partial<SubscriptionPlanDto>>(emptyForm);

  const createMutation = useCreateAdminPlanMutation({
    onSuccess: (res: any) => {
      success(res?.message || 'Plan created');
      showStripeWarnings(res);
      refetch();
      createModal.close();
      setForm(emptyForm);
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to create plan');
    },
  });

  const updateMutation = useUpdateAdminPlanMutation({
    onSuccess: (res: any) => {
      success(res?.message || 'Plan updated');
      showStripeWarnings(res);
      refetch();
      editModal.close();
      setForm(emptyForm);
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to update plan');
    },
  });

  const deleteMutation = useDeleteAdminPlanMutation({
    onSuccess: (res: any) => {
      success(res?.message || 'Plan successfully deleted / archived');
      refetch();
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to delete plan');
    },
  });

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to safe-delete/archive this plan? New signups or upgrades to this plan will be blocked, but existing subscriptions will continue normally.')) {
      deleteMutation.mutate({ id });
    }
  };

  const openCreate = () => {
    setForm(emptyForm);
    createModal.open(null);
  };

  const openEdit = (plan: SubscriptionPlanDto) => {
    setForm({ ...plan });
    editModal.open(plan);
  };

  const onChange = (key: keyof SubscriptionPlanDto, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submitCreate = () => {
    createMutation.mutate(form);
  };

  const submitUpdate = () => {
    const plan = editModal.data as SubscriptionPlanDto;
    updateMutation.mutate({ id: plan.id, data: form });
  };

  const editingPlan = editModal.data as SubscriptionPlanDto | null;

  // Drives the "this creates a new Stripe price" warning. Price amount, currency,
  // interval, and interval count force a new price — renaming or changing limits does not,
  // because those are not part of the immutable Stripe price object.
  const willReprice =
    !!editingPlan &&
    (Number(form.priceMonthly ?? 0) !== Number(editingPlan.priceMonthly ?? 0) ||
      String(form.currency || '').toLowerCase() !== String(editingPlan.currency || '').toLowerCase() ||
      String(form.billingInterval || 'month').toLowerCase() !== String(editingPlan.billingInterval || 'month').toLowerCase() ||
      Number(form.billingIntervalCount || 1) !== Number(editingPlan.billingIntervalCount || 1));

  const handleToggleActive = (plan: SubscriptionPlanDto) => {
    toggleConfirmModal.open(plan);
  };

  const confirmToggleActive = () => {
    const plan = toggleConfirmModal.data as SubscriptionPlanDto;
    updateMutation.mutate(
      { id: plan.id, data: { isActive: !plan.isActive } },
      {
        onSuccess: () => {
          toggleConfirmModal.close();
        },
      }
    );
  };

  if (error) {
    return (
      <div className="main-dashboard-div">
        <div className="dashboard-wrapper">
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            Failed to load subscription plans.
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="main-dashboard-div">
      <div className="dashboard-wrapper">
        <div
          className="heading-dashboard"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}
        >
          <h2>Plans & Pricing</h2>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Create Plan
          </Button>
        </div>

        <TableContainer component={Paper}>
          {isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography>Loading plans...</Typography>
            </Box>
          ) : plans.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No plans found</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Interval</TableCell>
                  <TableCell>Max Agents</TableCell>
                  <TableCell>Max Minutes / Month</TableCell>
                  <TableCell>Max SMS / Month</TableCell>
                  <TableCell>Sms_Number</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>Stripe</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.code}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>
                      {p.currency} {p.priceMonthly}
                    </TableCell>
                    <TableCell>
                      {(p.billingIntervalCount || 1) > 1 ? `${p.billingIntervalCount} ` : ''}
                      {p.billingInterval || 'month'}
                    </TableCell>
                    <TableCell>{p.maxAgents === null ? 'Unlimited' : p.maxAgents}</TableCell>
                    <TableCell>{p.maxMinutesPerMonth === null ? 'Unlimited' : p.maxMinutesPerMonth}</TableCell>
                    <TableCell>{p.maxSmsPerMonth === null ? 'Unlimited' : p.maxSmsPerMonth}</TableCell>
                    <TableCell>{p.maxSmsNumber ?? '-'}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getPlanDays(p.billingInterval, p.billingIntervalCount)}{' '}
                          {getPlanDays(p.billingInterval, p.billingIntervalCount) === 1 ? 'day' : 'days'}
                        </Typography>
                        {p.trialDurationDays ? (
                          <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600 }}>
                            Trial: {p.trialDurationDays} {p.trialDurationDays === 1 ? 'day' : 'days'}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <StripeLinkCell amount={p.priceMonthly} stripePriceId={p.stripePriceId} />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!p.isActive}
                        onChange={() => handleToggleActive(p)}
                        disabled={updateMutation.isLoading}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{p.updatedAt ? dayjs(p.updatedAt).format('MMM DD, YYYY') : '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => openEdit(p)} startIcon={<Edit />}>
                          Edit
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Create dialog */}
        <Dialog open={createModal.isOpen} onClose={createModal.close} maxWidth="md" fullWidth>
          <DialogTitle>Create Plan</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 1 }}>
              <TextField label="Code" value={form.code || ''} onChange={(e) => onChange('code', e.target.value)} />
              <TextField label="Name" value={form.name || ''} onChange={(e) => onChange('name', e.target.value)} />
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={form.currency || 'USD'}
                  label="Currency"
                  onChange={(e) => onChange('currency', e.target.value)}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <PriceField
                label="Price"
                value={form.priceMonthly}
                onChange={(v) => onChange('priceMonthly', v)}
                currency={form.currency || 'USD'}
                helperText="0 for a free or manually-assigned tier"
              />
              <FormControl fullWidth>
                <InputLabel>Billing Interval</InputLabel>
                <Select
                  value={form.billingInterval || 'month'}
                  label="Billing Interval"
                  onChange={(e) => onChange('billingInterval', e.target.value)}
                >
                  {BILLING_INTERVALS.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Interval Count"
                type="number"
                value={form.billingIntervalCount ?? 1}
                onChange={(e) => onChange('billingIntervalCount', e.target.value === '' ? 1 : Math.max(1, Number(e.target.value)))}
                helperText={`Equates to ${getPlanDays(form.billingInterval, form.billingIntervalCount ?? 1)} days per billing cycle`}
              />
              <TextField
                label="Max Agents (blank = unlimited)"
                type="number"
                value={form.maxAgents ?? ''}
                onChange={(e) => onChange('maxAgents', e.target.value === '' ? null : Number(e.target.value))}
              />
              <TextField
                label="Max Minutes/Month (blank = unlimited)"
                type="number"
                value={form.maxMinutesPerMonth ?? ''}
                onChange={(e) => onChange('maxMinutesPerMonth', e.target.value === '' ? null : Number(e.target.value))}
              />
              <TextField
                label="Max SMS/Month (blank = unlimited)"
                type="number"
                value={form.maxSmsPerMonth ?? ''}
                onChange={(e) => onChange('maxSmsPerMonth', e.target.value === '' ? null : Number(e.target.value))}
              />
              <TextField
                label="Max SMS Number"
                type="number"
                value={form.maxSmsNumber ?? ''}
                onChange={(e) => onChange('maxSmsNumber', e.target.value === '' ? null : Number(e.target.value))}
              />
              <TextField
                label="Trial Duration (Days)"
                type="number"
                value={form.trialDurationDays ?? ''}
                onChange={(e) => onChange('trialDurationDays', e.target.value === '' ? null : Number(e.target.value))}
                helperText="Optional trial period duration in days"
              />
              <FormControlLabel
                control={<Switch checked={!!form.isActive} onChange={(e) => onChange('isActive', e.target.checked)} />}
                label="Active"
              />
              <StripeCatalogField
                kind="plan"
                value={form.stripePriceId}
                onChange={(v) => onChange('stripePriceId', v)}
                amount={Number(form.priceMonthly ?? 0)}
                currency={form.currency || 'USD'}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={createModal.close}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submitCreate}
              disabled={createMutation.isLoading || !form.code || !form.name}
            >
              {createMutation.isLoading ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit dialog */}
        <Dialog open={editModal.isOpen} onClose={editModal.close} maxWidth="md" fullWidth>
          <DialogTitle>Edit Plan</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 1 }}>
              <TextField label="Code" value={form.code || ''} onChange={(e) => onChange('code', e.target.value)} />
              <TextField label="Name" value={form.name || ''} onChange={(e) => onChange('name', e.target.value)} />
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={form.currency || 'USD'}
                  label="Currency"
                  onChange={(e) => onChange('currency', e.target.value)}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <PriceField
                label="Price"
                value={form.priceMonthly}
                onChange={(v) => onChange('priceMonthly', v)}
                currency={form.currency || 'USD'}
                helperText="0 for a free or manually-assigned tier"
              />
              <FormControl fullWidth>
                <InputLabel>Billing Interval</InputLabel>
                <Select
                  value={form.billingInterval || 'month'}
                  label="Billing Interval"
                  onChange={(e) => onChange('billingInterval', e.target.value)}
                >
                  {BILLING_INTERVALS.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Interval Count"
                type="number"
                value={form.billingIntervalCount ?? 1}
                onChange={(e) => onChange('billingIntervalCount', e.target.value === '' ? 1 : Math.max(1, Number(e.target.value)))}
                helperText={`Equates to ${getPlanDays(form.billingInterval, form.billingIntervalCount ?? 1)} days per billing cycle`}
              />
              <TextField
                label="Max Agents (blank = unlimited)"
                type="number"
                value={form.maxAgents ?? ''}
                onChange={(e) => onChange('maxAgents', e.target.value === '' ? null : Number(e.target.value))}
              />
              <TextField
                label="Max Minutes/Month (blank = unlimited)"
                type="number"
                value={form.maxMinutesPerMonth ?? ''}
                onChange={(e) => onChange('maxMinutesPerMonth', e.target.value === '' ? null : Number(e.target.value))}
              />
              <TextField
                label="Max SMS/Month (blank = unlimited)"
                type="number"
                value={form.maxSmsPerMonth ?? ''}
                onChange={(e) => onChange('maxSmsPerMonth', e.target.value === '' ? null : Number(e.target.value))}
              />
              <TextField
                label="Max SMS Number"
                type="number"
                value={form.maxSmsNumber ?? ''}
                onChange={(e) => onChange('maxSmsNumber', e.target.value === '' ? null : Number(e.target.value))}
              />
              <TextField
                label="Trial Duration (Days)"
                type="number"
                value={form.trialDurationDays ?? ''}
                onChange={(e) => onChange('trialDurationDays', e.target.value === '' ? null : Number(e.target.value))}
                helperText="Optional trial period duration in days"
              />
              <FormControlLabel
                control={<Switch checked={!!form.isActive} onChange={(e) => onChange('isActive', e.target.checked)} />}
                label="Active"
              />
              <StripeCatalogField
                kind="plan"
                value={form.stripePriceId}
                onChange={(v) => onChange('stripePriceId', v)}
                amount={Number(form.priceMonthly ?? 0)}
                currency={form.currency || 'USD'}
                savedPriceId={editingPlan?.stripePriceId}
                savedProductId={editingPlan?.stripeProductId}
                willReprice={willReprice}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={editModal.close}>Cancel</Button>
            <Button variant="contained" onClick={submitUpdate} disabled={updateMutation.isLoading}>
              {updateMutation.isLoading ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status toggle warning dialog */}
        <Dialog
          open={toggleConfirmModal.isOpen}
          onClose={toggleConfirmModal.close}
          PaperProps={{
            sx: {
              borderRadius: '20px',
              padding: '32px 24px',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              maxWidth: '440px',
              width: '100%',
              overflow: 'hidden',
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 1 }}>
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                background: (toggleConfirmModal.data as SubscriptionPlanDto)?.isActive
                  ? 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)'
                  : 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                color: (toggleConfirmModal.data as SubscriptionPlanDto)?.isActive ? '#e53935' : '#43a047',
                boxShadow: (toggleConfirmModal.data as SubscriptionPlanDto)?.isActive
                  ? '0 8px 20px rgba(229, 57, 53, 0.15)'
                  : '0 8px 20px rgba(67, 160, 71, 0.15)',
              }}
            >
              {(toggleConfirmModal.data as SubscriptionPlanDto)?.isActive ? (
                <WarningAmberOutlined sx={{ fontSize: 32 }} />
              ) : (
                <CheckCircleOutlined sx={{ fontSize: 32 }} />
              )}
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5 }}>
              {(toggleConfirmModal.data as SubscriptionPlanDto)?.isActive ? 'Deactivate Plan?' : 'Activate Plan?'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.6, fontSize: '0.975rem' }}>
              Are you sure you want to{' '}
              {(toggleConfirmModal.data as SubscriptionPlanDto)?.isActive ? 'deactivate' : 'activate'} the plan{' '}
              <strong>{(toggleConfirmModal.data as SubscriptionPlanDto)?.name}</strong>?
            </Typography>
          </Box>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, mt: 3, px: 0, pb: 0 }}>
            <Button
              onClick={toggleConfirmModal.close}
              sx={{
                borderRadius: '10px',
                px: 3,
                py: 1,
                color: '#64748b',
                border: '1px solid #cbd5e1',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: '100px',
                '&:hover': {
                  background: '#f8fafc',
                  borderColor: '#94a3b8',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={confirmToggleActive}
              disabled={updateMutation.isLoading}
              sx={{
                borderRadius: '10px',
                px: 4,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
                minWidth: '100px',
                background: (toggleConfirmModal.data as SubscriptionPlanDto)?.isActive
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: (toggleConfirmModal.data as SubscriptionPlanDto)?.isActive
                  ? '0 4px 14px rgba(239, 68, 68, 0.3)'
                  : '0 4px 14px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  background: (toggleConfirmModal.data as SubscriptionPlanDto)?.isActive
                    ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                    : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                },
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Plans;
